import PolarisScenarioSet from 'dashboard/api/polaris/scenarioSet';
import { throwErrorMessage } from 'dashboard/store/utils/api';

// The Max's script (scenario set) of the assistant being looked at. One assistant at a time: `assistantId` says
// whose it is, so a page can tell when what is in the store is not for the assistant it shows.
const emptyValidation = () => ({ valid: true, errors: [] });
const emptyCatalog = () => ({
  tools: [],
  facts: [],
  predicates: [],
  importable: [],
});

export const state = {
  assistantId: null,
  set: null,
  nodes: [],
  validation: emptyValidation(),
  catalog: emptyCatalog(),
  uiFlags: { fetching: false, importing: false, savingNodeIds: [] },
};

export const getters = {
  getAssistantId: $state => $state.assistantId,
  getSet: $state => $state.set,
  hasSet: $state => !!$state.set,
  getNodes: $state =>
    [...$state.nodes].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
  getValidation: $state => $state.validation,
  getCatalog: $state => $state.catalog,
  getUIFlags: $state => $state.uiFlags,
};

// A node answered by the server may come as the whole record ({ id, title, node, errors }) or as the step's hash
// alone; either way the record keeps its identity and takes what came.
const mergeNode = (record, answered) =>
  answered && typeof answered === 'object' && 'node' in answered
    ? { ...record, ...answered }
    : { ...record, node: answered || record.node };

export const mutations = {
  SET_SCENARIO_SET_FLAGS($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  SET_SCENARIO_SET($state, { assistantId, data }) {
    $state.assistantId = assistantId;
    $state.set = data?.set || null;
    $state.nodes = Array.isArray(data?.nodes) ? data.nodes : [];
    $state.validation = data?.validation || emptyValidation();
    $state.catalog = { ...emptyCatalog(), ...(data?.catalog || {}) };
  },
  CLEAR_SCENARIO_SET($state, assistantId) {
    $state.assistantId = assistantId;
    $state.set = null;
    $state.nodes = [];
    $state.validation = emptyValidation();
    $state.catalog = emptyCatalog();
  },
  SET_SCENARIO_VALIDATION($state, validation) {
    if (validation) $state.validation = validation;
  },
  UPDATE_SCENARIO_NODE(
    $state,
    { id, node, validation, lockVersion, draftSha }
  ) {
    $state.nodes = $state.nodes.map(record =>
      record.id === id ? mergeNode(record, node) : record
    );
    if (validation) $state.validation = validation;
    // the answer says the set's new version: the next save must carry it, or it would be a false conflict
    if ($state.set) {
      $state.set = {
        ...$state.set,
        dirty: true,
        ...(lockVersion !== undefined && { lock_version: lockVersion }),
        ...(draftSha !== undefined && { draft_sha: draftSha }),
      };
    }
  },
};

// The panel and the page both ask for the set when a page opens: the second one shares the first one's answer.
let inflight = null;

const fetchSet = async (commit, assistantId) => {
  commit('SET_SCENARIO_SET_FLAGS', { fetching: true });
  commit('CLEAR_SCENARIO_SET', assistantId);
  try {
    const { data } = await PolarisScenarioSet.show(assistantId);
    commit('SET_SCENARIO_SET', { assistantId, data });
    return data;
  } catch (error) {
    return throwErrorMessage(error);
  } finally {
    commit('SET_SCENARIO_SET_FLAGS', { fetching: false });
    inflight = null;
  }
};

export const actions = {
  get: ({ commit }, assistantId) => {
    if (inflight?.assistantId === assistantId) return inflight.promise;
    inflight = { assistantId, promise: fetchSet(commit, assistantId) };
    return inflight.promise;
  },

  import: async ({ commit }, { assistantId, key }) => {
    commit('SET_SCENARIO_SET_FLAGS', { importing: true });
    try {
      const { data } = await PolarisScenarioSet.import(assistantId, key);
      commit('SET_SCENARIO_SET', { assistantId, data });
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit('SET_SCENARIO_SET_FLAGS', { importing: false });
    }
  },

  // Rejects with the request error: a 409 means another administrator saved the step first (the page reloads).
  saveNode: async (
    { commit, state: $state },
    { assistantId, id, title, node }
  ) => {
    commit('SET_SCENARIO_SET_FLAGS', {
      savingNodeIds: [...$state.uiFlags.savingNodeIds, id],
    });
    try {
      const { data } = await PolarisScenarioSet.updateNode(assistantId, id, {
        title,
        node,
        lockVersion: $state.set?.lock_version,
      });
      commit('UPDATE_SCENARIO_NODE', {
        id,
        node:
          data.node && 'node' in data.node
            ? data.node
            : { title, node: data.node || node },
        validation: data.validation,
        lockVersion: data.lock_version,
        draftSha: data.draft_sha,
      });
      return data;
    } finally {
      commit('SET_SCENARIO_SET_FLAGS', {
        savingNodeIds: $state.uiFlags.savingNodeIds.filter(item => item !== id),
      });
    }
  },
};

export default { namespaced: true, state, getters, mutations, actions };
